import React, { memo } from "react";

import BuildIcon from "@mui/icons-material/Build";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CodeIcon from "@mui/icons-material/Code";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import {
  Box,
  Divider,
  Stack,
  Tooltip,
  Typography,
  alpha,
  keyframes,
  useTheme,
} from "@mui/material";
import { Handle, Position, type NodeProps } from "@xyflow/react";

import type { PipelineNodeData } from "src/domains/application/hooks/usePipelineGraph";

// ── Constants ─────────────────────────────────────────────────────────────────

const ICON_MAP: Record<string, React.ReactElement> = {
  Code: <CodeIcon />,
  Build: <BuildIcon />,
  RocketLaunch: <RocketLaunchIcon />,
  CloudUpload: <CloudUploadIcon />,
  OpenInNew: <OpenInNewIcon />,
};

const STATUS_DOT_COLOR: Record<string, string> = {
  completed: "success.main",
  in_progress: "info.main",
  failed: "error.main",
  pending: "grey.400",
};

const STATUS_LABEL: Record<string, string> = {
  completed: "Done",
  in_progress: "Running",
  failed: "Failed",
  pending: "Queued",
};

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Format event type for compact display: "deployment.build_started" → "Build started" */
function formatEventLabel(eventType: string): string {
  const cleaned = eventType.replace(/^deployment\./, "").replace(/_/g, " ");
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

import { formatRelativeTime } from "./shared/pipeline-helpers";

// ── Component ─────────────────────────────────────────────────────────────────

function PipelineCustomNodeInner({
  data,
}: NodeProps & { data: PipelineNodeData }) {
  const theme = useTheme();
  const {
    label,
    nodeType,
    status,
    iconName,
    serviceName,
    isStale,
    deployModeBadge,
  } = data;

  const isVcsBadge = nodeType === "vcs-badge";
  const isFailed = status === "failed";
  const isInProgress = status === "in_progress";
  const isCompleted = status === "completed";

  const borderColor = isFailed
    ? theme.palette.error.main
    : isInProgress
      ? theme.palette.info.main
      : isCompleted
        ? theme.palette.success.main
        : theme.palette.divider;

  const iconBg = isFailed
    ? theme.palette.error.main
    : isInProgress
      ? theme.palette.info.main
      : isCompleted
        ? theme.palette.success.main
        : theme.palette.grey[300];

  const dotColorKey = STATUS_DOT_COLOR[status] ?? "grey.400";
  // Resolve MUI theme path like "success.main" → actual color
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const palette = theme.palette as any;
  const dotColor = dotColorKey.includes(".")
    ? (() => {
        const [paletteName, shade] = dotColorKey.split(".");
        return palette[paletteName]?.[shade] ?? theme.palette.grey[400];
      })()
    : (palette[dotColorKey] ?? theme.palette.grey[400]);

  const staleGlow = isStale
    ? `0 0 0 3px ${alpha(theme.palette.warning.main, 0.3)}, 0 0 8px ${alpha(theme.palette.warning.main, 0.2)}`
    : "none";

  return (
    <>
      {/* Left handle (target) */}
      <Handle
        type="target"
        position={Position.Left}
        style={{
          background: borderColor,
          width: 8,
          height: 8,
          border: "none",
        }}
      />

      <Box
        data-node-type={nodeType}
        data-service={serviceName}
        data-status={status}
        sx={{
          position: "relative",
          minWidth: isVcsBadge ? 210 : 190,
          maxWidth: 320,
          border: `1px solid ${borderColor}`,
          borderRadius: 2,
          bgcolor: theme.palette.background.paper,
          boxShadow: staleGlow !== "none" ? staleGlow : 1,
          cursor: "pointer",
          transition: "border-color 0.3s, box-shadow 0.3s",
          overflow: "hidden",
          "&:hover": {
            boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}, ${staleGlow !== "none" ? staleGlow : 2}`,
          },
        }}
      >
        {/* ── Main content row ── */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{
            px: 1.5,
            py: 1,
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* Icon */}
          <Box
            sx={{
              width: isVcsBadge ? 28 : 22,
              height: isVcsBadge ? 28 : 22,
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: iconBg,
              color: "white",
              fontSize: isVcsBadge ? 16 : 13,
              flexShrink: 0,
              ...(isInProgress && {
                animation: `${pulse} 1.5s infinite`,
              }),
            }}
          >
            {ICON_MAP[iconName] ?? <BuildIcon />}
          </Box>

          {/* Label */}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography
                component="div"
                variant={isVcsBadge ? "subtitle2" : "body2"}
                fontWeight={isVcsBadge ? 700 : 600}
                sx={{ lineHeight: 1.3 }}
                noWrap
              >
                {serviceName ?? label}
              </Typography>

              {/* Deploy mode badge — subtle inline */}
              {deployModeBadge && (
                <Typography
                  variant="caption"
                  sx={{
                    color:
                      deployModeBadge === "CI"
                        ? "warning.main"
                        : deployModeBadge === "External"
                          ? "text.disabled"
                          : "primary.main",
                    fontSize: "0.6rem",
                    fontWeight: 600,
                    lineHeight: 1,
                    border: `1px solid`,
                    borderColor:
                      deployModeBadge === "CI"
                        ? "warning.main"
                        : deployModeBadge === "External"
                          ? "text.disabled"
                          : "primary.main",
                    borderRadius: 0.5,
                    px: 0.5,
                    py: 0,
                  }}
                >
                  {deployModeBadge}
                </Typography>
              )}
            </Box>

            {/* Phase message */}
            {isVcsBadge && data.message && (
              <Typography
                component="div"
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.25, lineHeight: 1.3 }}
              >
                {isStale ? "No recent activity" : data.message}
              </Typography>
            )}
          </Box>

          {/* Status dot + label */}
          <Tooltip
            title={
              isStale
                ? "No activity for 15+ minutes"
                : (STATUS_LABEL[status] ?? status)
            }
          >
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{ flexShrink: 0 }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: dotColor,
                  ...(isInProgress && {
                    animation: `${pulse} 1.5s infinite`,
                  }),
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.65rem",
                  color: isFailed
                    ? "error.main"
                    : isInProgress
                      ? "info.main"
                      : isCompleted
                        ? "success.main"
                        : "text.secondary",
                  fontWeight: 600,
                }}
              >
                {STATUS_LABEL[status] ?? status}
              </Typography>
            </Stack>
          </Tooltip>
        </Stack>

        {/* ── Step bullets ── */}
        {data.steps && data.steps.length > 0 && (
          <Stack
            direction="column"
            spacing={0.25}
            sx={{
              px: 1.5,
              pb: data.recentEvents && data.recentEvents.length > 0 ? 0.5 : 1,
              position: "relative",
              zIndex: 1,
            }}
          >
            {data.steps.map((s) => {
              const bColor =
                s.status === "completed"
                  ? theme.palette.success.main
                  : s.status === "in_progress"
                    ? theme.palette.info.main
                    : s.status === "failed"
                      ? theme.palette.error.main
                      : theme.palette.grey[400];

              const statusIcon =
                s.status === "completed"
                  ? "✓"
                  : s.status === "failed"
                    ? "✗"
                    : s.status === "in_progress"
                      ? "›"
                      : "○";

              const tooltipText = s.message
                ? `${s.name}: ${s.status} — ${s.message}`
                : `${s.name}: ${s.status}`;

              return (
                <Tooltip key={s.name} title={tooltipText}>
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      fontSize: "0.6rem",
                      lineHeight: 1.2,
                      color: "text.primary",
                      fontWeight: 500,
                      ...(s.status === "in_progress" && {
                        animation: `${pulse} 1.5s infinite`,
                      }),
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        width: 5,
                        height: 5,
                        borderRadius: "50%",
                        bgcolor: bColor,
                        flexShrink: 0,
                      }}
                    />
                    <Box component="span" sx={{ opacity: 0.65 }}>
                      {statusIcon}
                    </Box>
                    {s.name}
                  </Box>
                </Tooltip>
              );
            })}
          </Stack>
        )}

        {/* ── Inline event summary (last 2 events) ── */}
        {data.recentEvents && data.recentEvents.length > 0 && !isVcsBadge && (
          <>
            <Divider sx={{ mx: 1.5, borderColor: "divider" }} />
            <Stack
              direction="column"
              spacing={0.25}
              sx={{
                px: 1.5,
                pb: 1,
                pt: 0.5,
                position: "relative",
                zIndex: 1,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.5rem",
                  fontWeight: 600,
                  color: "text.disabled",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  lineHeight: 1,
                  mb: 0.25,
                }}
              >
                Events
              </Typography>
              {data.recentEvents.slice(0, 2).map((evt, idx) => {
                const evtColor =
                  evt.status === "completed"
                    ? theme.palette.success.main
                    : evt.status === "failed"
                      ? theme.palette.error.main
                      : evt.status === "in_progress"
                        ? theme.palette.info.main
                        : theme.palette.grey[400];

                return (
                  <Box
                    key={`${evt.eventType}-${idx}`}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      fontSize: "0.55rem",
                      lineHeight: 1.2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 4,
                        height: 4,
                        borderRadius: "50%",
                        bgcolor: evtColor,
                        flexShrink: 0,
                        ...(evt.status === "in_progress" && {
                          animation: `${pulse} 1.5s infinite`,
                        }),
                      }}
                    />
                    <Tooltip title={evt.message || evt.eventType}>
                      <Typography
                        variant="inherit"
                        component="span"
                        sx={{
                          color: "text.secondary",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: 160,
                        }}
                      >
                        {formatEventLabel(evt.eventType)}
                      </Typography>
                    </Tooltip>
                    <Typography
                      variant="inherit"
                      component="span"
                      sx={{ color: "text.disabled", ml: "auto", flexShrink: 0 }}
                    >
                      {formatRelativeTime(evt.timestamp)}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          </>
        )}
      </Box>

      {/* Right handle (source) */}
      <Handle
        type="source"
        position={Position.Right}
        style={{
          background: borderColor,
          width: 8,
          height: 8,
          border: "none",
        }}
      />
    </>
  );
}

export const PipelineCustomNode = memo(
  PipelineCustomNodeInner,
) as typeof PipelineCustomNodeInner;
